import { cn } from '@/lib/utils';

import Button, { ButtonProps } from '@/components/atoms/Button';
import { animatedIconRightStyles } from '@/components/atoms/Button/styles';
import Card, { CardProps } from '@/components/atoms/Card';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { transformLinkToButtonProps } from '@/components/atoms/LinkButton/utils';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import { SvgIcon } from '@/components/SvgIcon';

import { SCRAPING_CARD_DATA_TRACKER_COMPONENT } from '@/blocks/ScrapingTemplateLibrary/consts';
import { BlockReferenceArray } from '@/cms/types/block';
import {
  UniversalCard,
  UniversalCardProps,
} from '@/features/cards/UniversalCard/UniversalCard';

import iconChevronRightSrc from '~/svg/IconChevronRight.svg';

type TagProps = {
  title: string;
};

export type ScrapingTemplateCardProps = {
  universalCard?: UniversalCardProps;
  title: string;
  image: ImageStrapiProps;
  description: RichTextFieldStrapiProps;
  cta: LinkButtonStrapiProps;
  scraping_template_tags: BlockReferenceArray<TagProps>;
  cardProps?: CardProps;
  classNames?: {
    root?: string;
    content?: string;
    card?: CardProps['classNames'];
    button?: ButtonProps['classNames'];
  };
};

/**
 * Span styled like a button for SEO
 */
const StyledSpan = ({ text }: { text?: string }) => {
  return (
    <span
      className={cn(
        'group mt-6 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[4px] px-6',
        'font-serif text-[1rem] font-medium transition-colors',
        'text-base-dark-white bg-brand-500 hover:bg-brand-600 active:bg-brand-700'
      )}
    >
      <span className='flex h-full items-center justify-center whitespace-nowrap'>
        {text}
      </span>
      <SvgIcon src={iconChevronRightSrc} className={animatedIconRightStyles} />
    </span>
  );
};

const ScrapingTemplateCard = ({
  universalCard,
  title,
  image,
  description,
  cta,
  ...rest
}: ScrapingTemplateCardProps) => {
  if (universalCard) {
    return (
      <div data-tracker-component={SCRAPING_CARD_DATA_TRACKER_COMPONENT}>
        <UniversalCard
          hasShadow={false}
          {...universalCard}
          className={cn(
            rest.classNames?.card?.root,
            rest?.cardProps?.classNames?.root
          )}
        />
      </div>
    );
  }


  const { href, target, element } = transformLinkToButtonProps(cta || {});

  return (
    <Button
      isDecoro={true}
      as={element}
      href={href}
      target={target}
      noStyles
      className={cn('h-full', rest.classNames?.card?.root)}
      data-tracker-component={SCRAPING_CARD_DATA_TRACKER_COMPONENT}
    >
      <Card
        hasHoverEffect
        hasShadow
        {...rest.cardProps}
        classNames={{
          ...rest?.cardProps?.classNames,
          ...rest.classNames?.card,
          root: cn(
            'px-6 py-8 flex flex-col flex-1 h-full content-between hover:bg-brand-25 dark:border-gray-dark-600',
            'dark:hover:bg-brand-975',
            rest.classNames?.card?.root,
            rest?.cardProps?.classNames?.root
          ),
        }}
      >
        <div className='flex flex-1 flex-col'>
          <div className='mb-4 flex items-center'>
            <ImageStrapi
              inner={{
                classNames: {
                  image: 'w-8 h-8 rounded-full mr-3',
                },
              }}
              block={image}
            />
            <Text className='text-h3'>{title}</Text>
          </div>

          <div
            className={cn(
              'text-body-l dark:text-gray-dark-400 min-h-24 overflow-hidden font-mono text-gray-500',
              rest.classNames?.content
            )}
          >
            <RichTextFieldStrapi block={description} />
          </div>
        </div>
        {cta?.text && <StyledSpan text={cta.text} />}
      </Card>
    </Button>
  );
};

export default ScrapingTemplateCard;
